/**
 * Time helpers shared by the booking components.
 *
 * These live here rather than inside one component so that two surfaces that
 * look the same to a user cannot drift apart in what they actually book.
 *
 * Every helper takes the clock reading as an argument (defaulting to
 * ``Date.now()``) rather than reading it internally at an unpredictable
 * moment. A quick-book control has two distinct moments — the one it was
 * *drawn* at and the one it was *pressed* at — and the whole point of #344 is
 * that they are not the same moment. Passing the reading in makes which one is
 * meant explicit at each call site (and leaves the helpers pure and testable).
 */

/** Booking the in-progress slot should start at "now", not the slot's :00
 *  boundary (so a 15:04 booking of the 15:00 quarter starts at 15:04). Clamps
 *  any start that's already passed up to now; future starts are untouched, so
 *  only the current slot is ever affected.
 *
 *  Every booking start goes through this: ``RoomDaySchedule``'s drag-to-book
 *  release directly, and both sets of quick-book buttons via
 *  ``quickBookWindow`` below — they are visually the same control and must
 *  book the same time. */
export function clampStartToNow(iso: string, nowMs: number = Date.now()): string {
  return new Date(iso).getTime() < nowMs ? new Date(nowMs).toISOString() : iso;
}

/** Where a quick-book press lands: the start it would book, and how much of
 *  the free slot is still ahead of that start.
 *
 *  For a slot that hasn't begun the start is the slot's own and the remaining
 *  time is its full length; for one already under way both are measured from
 *  ``nowMs``; for one already over the remaining time is zero. */
function quickBookWindow(
  slotStart: string,
  slotDurationMinutes: number,
  nowMs: number,
): { start: string; remainingMinutes: number } {
  const start = clampStartToNow(slotStart, nowMs);
  const slotEndMs = new Date(slotStart).getTime() + slotDurationMinutes * 60_000;
  const remainingMinutes = Math.floor((slotEndMs - new Date(start).getTime()) / 60_000);
  return { start, remainingMinutes: Math.max(0, remainingMinutes) };
}

/** The durations worth offering for a free slot, as of ``nowMs``.
 *
 *  Measured from where the booking would actually start, not from the slot's
 *  nominal start: four minutes into a 30-minute slot only 26 remain, so ``30m``
 *  is no longer offered. Offering it would either overrun the slot or produce a
 *  26-minute booking wearing a 30-minute label — both were live before #344. */
export function quickBookDurations(
  durations: number[],
  slotStart: string,
  slotDurationMinutes: number,
  nowMs: number = Date.now(),
): number[] {
  const { remainingMinutes } = quickBookWindow(slotStart, slotDurationMinutes, nowMs);
  return durations.filter((d) => d <= remainingMinutes);
}

/** Resolve a quick-book press into the window it books, against the clock at
 *  press time.
 *
 *  Returns ``null`` when the duration no longer fits — the button was drawn
 *  against an older reading of the clock (a kiosk or a tab left open) and the
 *  slot has run down since. Callers re-render off the fresh reading, so the
 *  stale button disappears rather than booking into someone else's meeting. */
export function resolveQuickBooking(
  slotStart: string,
  slotDurationMinutes: number,
  durationMinutes: number,
  nowMs: number = Date.now(),
): { start: string; end: string } | null {
  const { start, remainingMinutes } = quickBookWindow(slotStart, slotDurationMinutes, nowMs);
  if (durationMinutes > remainingMinutes) return null;
  return {
    start,
    end: new Date(new Date(start).getTime() + durationMinutes * 60_000).toISOString(),
  };
}
